import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { breakpoints, colors } from '@/styles'
import { PageTitle } from '@/features/Application/elements'
import { showInfoModal, getModalInfo } from '@/features/Application/info-modal'
import { View as CreditSummaryView } from '@/features/Application/credit-summary'
import { View as RiskAssessmentView } from '@/features/Application/risk-assessment'
import { View as TrustworthinessView } from '@/features/Application/trustworthiness'
import { View as RentToIncomeView } from '@/features/Application/rent-to-income'
import TrustScoreSummary from './trust-score-summary'

const Wrapper = styled.div`
  position: relative;
  max-width: 900px;
  background: ${colors.grey96};
  border: 1px solid ${colors.grey88};
  border-radius: 0px 8px 8px 8px;
  padding: 45px 20px 20px;
  margin-top: 59px;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    padding: unset;
    border: none;
    border-radius: none;
    margin-top: unset;
    background: #f8f8f8;
    margin-top: unset;
    border-radius: 8px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin-top: 40px;
  }
`

const TrustScore = ({ application = {}, hasUnlockCredits }) => {
  const { t } = useTranslation()
  const { title, content, mobileContent, isPoweredByEquifax } = getModalInfo('trust-score', t)
  const handleClick = () =>
    showInfoModal({
      title,
      content,
      mobileContent,
      isPoweredByEquifax
    })
  const { score, rate, state, status, is_request_queued } = application?.trust_score ?? {}
  const isLocked =
    application?.trust_score?.state === 'locked' || application?.trust_score?.state === 'analyzing'
  const isLoading = !!application?.trust_score?.loading

  const userInfo = application?.user_info ?? {}
  const applicantFullName = `${userInfo?.first_name} ${userInfo?.last_name}`

  return (
    <Wrapper id="trust-score">
      <PageTitle
        title={t('b.application.view.tenancy_application.liv_score.title', 'Trust Score')}
        handleClick={handleClick}
        showInfo
        showPoweredBy
        clickId="application-trust-score-info-score"
      />

      <TrustScoreSummary
        score={score}
        rate={rate}
        state={state}
        status={status}
        trustScore={application?.trust_score}
        applicationId={application?.id}
        reason={application?.user_questions?.no_income_reason?.answer}
        applicantName={`${userInfo.preferred_name} ${userInfo.last_name?.substring(0, 1)}`}
        applicantFullName={applicantFullName}
        hasUnlockCredits={hasUnlockCredits}
        isLoading={isLoading}
        requestQueued={is_request_queued}
      />

      <CreditSummaryView
        data={{
          ...application?.trust_score?.credit_summary,
          credit_score_range: application?.trust_score?.credit_score_range,
          status: application?.trust_score?.status
        }}
        application={application}
        isLocked={is_request_queued ? true : isLocked}
        isLoading={isLoading}
      />
      <RiskAssessmentView
        data={application?.trust_score?.risk_assessment}
        application={application}
        isLocked={isLocked}
        isLoading={isLoading}
        applicantFullName={applicantFullName}
        hasUnlockCredits={hasUnlockCredits}
      />
      <TrustworthinessView
        data={application?.trust_score?.trustworthiness}
        application={application}
        isLocked={isLocked}
        isLoading={isLoading}
      />
      <RentToIncomeView
        data={application?.trust_score?.income_to_rent_ratio}
        application={application}
        isLocked={isLocked}
        isLoading={isLoading}
      />
    </Wrapper>
  )
}

export default TrustScore
